<script setup lang="ts">
  /**
   * PDFViewer Component
   * A reusable component for displaying PDF documents in a modal dialog.
   *
   * Features:
   * - Modal PDF viewer with backdrop click to close
   * - Page navigation
   * - Text highlighting
   * - Loading state
   * - Responsive design
   *
   * @example
   * ```vue
   *   <PDFViewer
   *     v-model:isOpen="isOpen"
   *     v-model:url="pdfUrl"
   *     :filename="filename"
   *     :highlight-text="'text to highlight'"
   *     :initial-page="1"
   *     @loaded="(pdfInstance) => console.log('PDF loaded', pdfInstance)"
   *     @page-change="(page) => console.log('Page changed', page)"
   *   />
   * ```
   *
   * @typedef {Object} Props
   * @property {string} url - URL of the PDF file to display
   * @property {string} filename - Name of the file to display in the header
   * @property {boolean} isOpen - Controls visibility of the modal
   * @property {number} [initialPage=1] - Initial page number to display
   * @property {string} [highlightText] - Text to highlight in the PDF
   *
   * @typedef {Object} Emits
   * @property {(event: 'update:isOpen', value: boolean) => void} update:isOpen - Emitted when modal visibility changes
   * @property {(event: 'update:url', value: string) => void} update:url - Emitted when URL should be updated
   * @property {(event: 'loaded', pdfInstance: any) => void} loaded - Emitted when PDF is loaded
   * @property {(event: 'pageChange', page: number) => void} pageChange - Emitted when page changes
   * @property {(event: 'highlight', event: any) => void} highlight - Emitted on text highlight
   */

  import { ref, computed, watch } from 'vue'
  import { usePDF, VuePDF } from '@tato30/vue-pdf'
  import PDFNavigation from '@/components/pdf/genericPdfNav.vue'

  interface Props {
    url: string
    filename: string
    isOpen: boolean
    initialPage?: number
    highlightText?: string
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'update:url', value: string): void
    (e: 'loaded', pdfInstance: any): void
    (e: 'pageChange', page: number): void
    (e: 'highlight', event: any): void
  }>()

  const currentPage = ref(props.initialPage || 1)
  const isLoading = ref(true)
  const internalUrl = ref(props.url)

  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  // Watch for URL changes from parent
  watch(
    () => props.url,
    (newUrl) => {
      internalUrl.value = newUrl
    }
  )

  // Use internal URL for PDF loading
  const { pdf, pages } = usePDF(internalUrl)

  function closeViewer() {
    emit('update:isOpen', false)
    emit('update:url', '')
    internalUrl.value = ''
    currentPage.value = props.initialPage || 1
    isLoading.value = true
  }

  function onPdfLoaded(pdfInstance: any) {
    isLoading.value = false
    emit('loaded', pdfInstance)
  }

  function updateCurrentPage(newPage: number) {
    currentPage.value = newPage
    emit('pageChange', newPage)
  }

  function onHighlight(event: any) {
    emit('highlight', event)
  }

  function handleBackdropClick(event: MouseEvent) {
    // Only close if clicking directly on the backdrop
    if (event.target === event.currentTarget) {
      closeViewer()
    }
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click="handleBackdropClick"
  >
    <div class="flex h-[90vh] w-[40vw] flex-col rounded-lg bg-white p-4">
      <div class="mb-2 flex items-center justify-between">
        <div class="w-8"><!-- Spacer to balance the close button --></div>
        <h2 class="flex-1 text-center text-xl font-bold">{{ filename }}</h2>
        <button
          @click="closeViewer"
          class="h-8 w-8 rounded-full text-2xl text-gray-600 hover:bg-gray-100 hover:text-gray-800"
        >
          ×
        </button>
      </div>
      <div class="relative flex-grow overflow-auto">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-90"
        >
          <div class="text-xl font-semibold text-purple-600">Wird geladen...</div>
        </div>
        <VuePDF
          :pdf="pdf"
          :page="currentPage"
          :scale="1"
          @loaded="onPdfLoaded"
          :highlight-text="highlightText"
          @highlight="onHighlight"
          :highlight-options="highlightOptions"
          text-layer
        />
      </div>
      <div class="mt-4 flex items-center justify-center">
        <PDFNavigation
          :current-page="currentPage"
          :total-pages="pages"
          :show-prev-next-buttons="true"
          :show-page-buttons="true"
          @update-page="updateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>
